import React, { useCallback, useState, useRef, useEffect } from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Ticket, TicketTag, PRIORITY_LABELS } from './core/_models'
import { updateTicket, reorderTickets, switchToAccount, addTicketReply } from './core/_requests'
import moment from 'moment'
import 'moment/locale/he'
moment.locale('he')

interface KanbanBoardProps {
  tickets: Ticket[]
  tags: TicketTag[]
  onTicketClick: (ticketId: string) => void
  onRefresh: () => void
}

type KanbanStatus = 'open' | 'in_progress' | 'in_development' | 'waiting_on_customer' | 'follow_up' | 'resolved'

const KANBAN_COLUMNS: { status: KanbanStatus; label: string; color: string }[] = [
  { status: 'open', label: 'פתוח', color: '#f64e60' },
  { status: 'in_progress', label: 'בטיפול', color: '#ffa800' },
  { status: 'in_development', label: 'בטיפול פיתוח', color: '#3699ff' },
  { status: 'waiting_on_customer', label: 'ממתין ללקוח', color: '#8950fc' },
  { status: 'follow_up', label: 'מעקב', color: '#e67e22' },
  { status: 'resolved', label: 'נפתר', color: '#1bc5bd' },
]

const QUICK_FOLLOWUP_OPTIONS = [
  { label: 'עוד שעתיים', hours: 2 },
  { label: 'עוד 4 שעות', hours: 4 },
  { label: 'מחר', hours: 24 },
  { label: 'עוד יומיים', hours: 48 },
  { label: 'עוד שבוע', hours: 168 },
]

const ITEM_TYPE = 'TICKET_CARD'

interface DragItem {
  ticketId: string
  currentStatus: string
  index: number
}

// Draggable ticket card
const KanbanCard: React.FC<{
  ticket: Ticket
  tags: TicketTag[]
  index: number
  onClick: () => void
  onMoveCard: (dragIndex: number, hoverIndex: number, ticketId: string) => void
  onUpdateTicket: (ticketId: string, updates: Partial<Ticket>) => void
  onRefresh: () => void
}> = ({ ticket, tags, index, onClick, onMoveCard, onUpdateTicket, onRefresh }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [showNote, setShowNote] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [sendingNote, setSendingNote] = useState(false)
  const [showFollowUp, setShowFollowUp] = useState(false)
  const [followUpNoteText, setFollowUpNoteText] = useState('')

  const [{ isDragging }, drag] = useDrag(() => ({
    type: ITEM_TYPE,
    item: { ticketId: ticket._id, currentStatus: ticket.status, index } as DragItem,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [ticket._id, ticket.status, index])

  const [, drop] = useDrop(() => ({
    accept: ITEM_TYPE,
    hover(item: DragItem, monitor) {
      if (!ref.current) return
      if (item.currentStatus !== ticket.status) return // Only reorder within same column

      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      onMoveCard(dragIndex, hoverIndex, item.ticketId)
      item.index = hoverIndex
    },
  }), [index, ticket.status, onMoveCard])

  drag(drop(ref))

  const getCustomerName = () => {
    if (typeof ticket.account === 'object' && ticket.account) {
      return `${ticket.account.first_name || ''} ${ticket.account.last_name || ''}`.trim() || ticket.account.email
    }
    return 'Unknown'
  }

  const getAccountId = () => {
    if (typeof ticket.account === 'object' && ticket.account) {
      return ticket.account._id
    }
    if (typeof ticket.account === 'string') return ticket.account
    return null
  }

  const getAssigneeName = () => {
    if (ticket.assigned_to && typeof ticket.assigned_to === 'object') {
      return ticket.assigned_to.username
    }
    return null
  }

  const getTagObj = (tagName: string) => tags.find(t => t.name === tagName)

  const timeAgo = moment(ticket.updated_at).fromNow()
  const assignee = getAssigneeName()

  const handleLoginAsAccount = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const accountId = getAccountId()
    if (!accountId) return

    try {
      const res = await switchToAccount(accountId)
      const loginUrl = `${window.location.origin}/?overide-token=${res.data.token}`
      window.open(loginUrl, '_blank')
    } catch (err) {
      console.error('Failed to switch account:', err)
    }
  }

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!noteText.trim()) return

    setSendingNote(true)
    try {
      await addTicketReply(ticket._id, noteText.trim(), true)
      // Optimistic update - show note on card without full refresh
      onUpdateTicket(ticket._id, {
        last_internal_note: noteText.trim(),
        last_internal_note_at: new Date().toISOString(),
      })
      setNoteText('')
      setShowNote(false)
    } catch (err) {
      console.error('Failed to add note:', err)
    } finally {
      setSendingNote(false)
    }
  }

  const handleQuickFollowUp = async (e: React.MouseEvent, hours: number) => {
    e.stopPropagation()
    const followUpDate = moment().add(hours, 'hours').toISOString()
    const note = followUpNoteText.trim()
    try {
      await updateTicket(ticket._id, {
        follow_up_date: followUpDate,
        follow_up_note: note,
        status: 'follow_up' as any,
      })
      // Also add as internal note if there's a note
      if (note) {
        await addTicketReply(ticket._id, `מעקב: ${note} (${moment(followUpDate).fromNow()})`, true)
      }
      // Optimistic update
      onUpdateTicket(ticket._id, {
        follow_up_date: followUpDate,
        follow_up_note: note,
        status: 'follow_up',
        last_internal_note: note ? `מעקב: ${note} (${moment(followUpDate).fromNow()})` : ticket.last_internal_note,
        last_internal_note_at: note ? new Date().toISOString() : ticket.last_internal_note_at,
      })
      setShowFollowUp(false)
      setFollowUpNoteText('')
    } catch (err) {
      console.error('Failed to set follow-up:', err)
    }
  }

  return (
    <div
      ref={ref}
      className={`kanban-card ${isDragging ? 'dragging' : ''} ${ticket.admin_unread ? 'unread' : ''}`}
      onClick={onClick}
    >
      <div className="kanban-card-header">
        <span className="ticket-number">{ticket.ticket_number}</span>
        <div className="kanban-card-actions">
          {ticket.priority !== 'medium' && (
            <span className={`priority-badge ${ticket.priority}`}>
              {PRIORITY_LABELS[ticket.priority]}
            </span>
          )}
          {getAccountId() && (
            <button
              className="kanban-login-btn"
              onClick={handleLoginAsAccount}
              title="כניסה כלקוח"
            >
              <i className="bi bi-box-arrow-in-left"></i>
            </button>
          )}
        </div>
      </div>
      <div className="kanban-card-subject">{ticket.subject}</div>
      <div className="kanban-card-meta">
        <span className="customer-name">{getCustomerName()}</span>
      </div>
      <div className="kanban-card-dates">
        <span className="card-date" title="נוצר">
          <i className="bi bi-calendar-plus"></i>
          נוצר: {moment(ticket.created_at).format('DD/MM/YY HH:mm')}
        </span>
        <span className="card-date" title="נצפה לאחרונה">
          <i className="bi bi-eye"></i>
          נצפה: {timeAgo}
        </span>
      </div>
      <div className="kanban-card-footer">
        {ticket.tags.length > 0 && (
          <div className="kanban-tags">
            {ticket.tags.slice(0, 3).map((tagName, i) => {
              const tagObj = getTagObj(tagName)
              return (
                <span
                  key={i}
                  className="tag-chip"
                  style={tagObj ? { background: `${tagObj.color}20`, color: tagObj.color } : {}}
                >
                  {tagName}
                </span>
              )
            })}
          </div>
        )}
        {assignee && (
          <span className="assignee-avatar" title={assignee}>
            {assignee.charAt(0).toUpperCase()}
          </span>
        )}
        {ticket.follow_up_date && (
          <span className={`follow-up-icon ${new Date(ticket.follow_up_date) <= new Date() ? 'overdue' : ''}`} title={`מעקב: ${moment(ticket.follow_up_date).fromNow()}${ticket.follow_up_note ? '\n' + ticket.follow_up_note : ''}`}>
            <i className="bi bi-bell"></i>
          </span>
        )}
      </div>

      {/* Enhanced follow-up display for מעקב status */}
      {ticket.status === 'follow_up' && ticket.follow_up_date && (
        <div className={`kanban-card-followup ${new Date(ticket.follow_up_date) <= new Date() ? 'overdue' : 'upcoming'}`}>
          <div className="followup-header">
            <i className="bi bi-bell-fill"></i>
            <span className="followup-date">{moment(ticket.follow_up_date).fromNow()}</span>
            {new Date(ticket.follow_up_date) <= new Date() && <span className="followup-overdue-badge">עבר!</span>}
          </div>
          {ticket.follow_up_note && <div className="followup-note-full">{ticket.follow_up_note}</div>}
        </div>
      )}

      {/* Follow-up display for non-follow_up status */}
      {ticket.status !== 'follow_up' && ticket.follow_up_date && (
        <div className={`kanban-card-followup ${new Date(ticket.follow_up_date) <= new Date() ? 'overdue' : 'upcoming'}`}>
          <i className="bi bi-bell-fill"></i>
          <span className="followup-date">{moment(ticket.follow_up_date).fromNow()}</span>
          {ticket.follow_up_note && <span className="followup-note">{ticket.follow_up_note}</span>}
        </div>
      )}

      {/* Last internal note */}
      {ticket.last_internal_note && (
        <div className="kanban-card-last-note">
          <i className="bi bi-chat-left-text"></i>
          <span className="last-note-text">{ticket.last_internal_note}</span>
          {ticket.last_internal_note_at && (
            <span className="last-note-time">{moment(ticket.last_internal_note_at).fromNow()}</span>
          )}
        </div>
      )}

      {/* Card action buttons */}
      <div className="kanban-card-bottom-actions">
        <button
          className="kanban-action-btn"
          onClick={(e) => { e.stopPropagation(); setShowNote(!showNote); setShowFollowUp(false) }}
          title="הוסף הערה פנימית"
        >
          <i className="bi bi-chat-dots"></i>
        </button>
        <button
          className="kanban-action-btn"
          onClick={(e) => { e.stopPropagation(); setShowFollowUp(!showFollowUp); setShowNote(false) }}
          title="קבע מעקב"
        >
          <i className="bi bi-bell"></i>
        </button>
      </div>

      {/* Inline note form */}
      {showNote && (
        <form
          className="kanban-inline-note"
          onSubmit={handleAddNote}
          onClick={(e) => e.stopPropagation()}
        >
          <textarea
            placeholder="הערה פנימית (לא תישלח ללקוח)..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={2}
            autoFocus
          />
          <div className="inline-note-actions">
            <button type="submit" className="note-send-btn" disabled={sendingNote || !noteText.trim()}>
              {sendingNote ? '...' : 'שמור'}
            </button>
            <button type="button" className="note-cancel-btn" onClick={() => { setShowNote(false); setNoteText('') }}>
              ביטול
            </button>
          </div>
        </form>
      )}

      {/* Quick follow-up picker */}
      {showFollowUp && (
        <div className="kanban-followup-picker" onClick={(e) => e.stopPropagation()}>
          <input
            className="followup-note-input"
            type="text"
            placeholder="הערה למעקב..."
            value={followUpNoteText}
            onChange={(e) => setFollowUpNoteText(e.target.value)}
            autoFocus
          />
          <div className="followup-picker-label">קבע מעקב:</div>
          <div className="followup-picker-options">
            {QUICK_FOLLOWUP_OPTIONS.map((opt) => (
              <button
                key={opt.hours}
                className="followup-quick-btn"
                onClick={(e) => handleQuickFollowUp(e, opt.hours)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Droppable column
const KanbanColumn: React.FC<{
  status: KanbanStatus
  label: string
  color: string
  tickets: Ticket[]
  tags: TicketTag[]
  onTicketClick: (ticketId: string) => void
  onDrop: (ticketId: string, newStatus: KanbanStatus) => void
  onReorder: (status: KanbanStatus, tickets: Ticket[]) => void
  onUpdateTicket: (ticketId: string, updates: Partial<Ticket>) => void
  onRefresh: () => void
}> = ({ status, label, color, tickets, tags, onTicketClick, onDrop, onReorder, onUpdateTicket, onRefresh }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ITEM_TYPE,
    drop: (item: DragItem) => {
      if (item.currentStatus !== status) {
        onDrop(item.ticketId, status)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }), [status, onDrop])

  const moveCard = useCallback((dragIndex: number, hoverIndex: number, ticketId: string) => {
    const newTickets = [...tickets]
    const dragItem = newTickets.find(t => t._id === ticketId)
    if (!dragItem) return

    const dragIdx = newTickets.indexOf(dragItem)
    newTickets.splice(dragIdx, 1)
    newTickets.splice(hoverIndex, 0, dragItem)

    onReorder(status, newTickets)
  }, [tickets, status, onReorder])

  return (
    <div ref={drop} className={`kanban-column ${isOver ? 'drag-over' : ''}`}>
      <div className="kanban-column-header">
        <div className="column-title">
          <span className="column-dot" style={{ background: color }}></span>
          <span>{label}</span>
          <span className="column-count">{tickets.length}</span>
        </div>
      </div>
      <div className="kanban-column-body">
        {tickets.map((ticket, idx) => (
          <KanbanCard
            key={ticket._id}
            ticket={ticket}
            tags={tags}
            index={idx}
            onClick={() => onTicketClick(ticket._id)}
            onMoveCard={moveCard}
            onUpdateTicket={onUpdateTicket}
            onRefresh={onRefresh}
          />
        ))}
        {tickets.length === 0 && (
          <div className="kanban-empty">
            <span>אין פניות</span>
          </div>
        )}
      </div>
    </div>
  )
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ tickets, tags, onTicketClick, onRefresh }) => {
  const [localTickets, setLocalTickets] = useState<Ticket[]>(tickets)
  const reorderTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Sync local state with props
  useEffect(() => {
    setLocalTickets(tickets)
  }, [tickets])

  const handleDrop = useCallback(async (ticketId: string, newStatus: KanbanStatus) => {
    try {
      await updateTicket(ticketId, { status: newStatus })
      onRefresh()
    } catch (error) {
      console.error('Failed to update ticket status:', error)
    }
  }, [onRefresh])

  const handleReorder = useCallback((status: KanbanStatus, reorderedTickets: Ticket[]) => {
    // Update local state immediately for smooth DnD
    setLocalTickets(prev => {
      const otherTickets = prev.filter(t => t.status !== status)
      return [...otherTickets, ...reorderedTickets]
    })

    // Debounce API call
    if (reorderTimeoutRef.current) {
      clearTimeout(reorderTimeoutRef.current)
    }
    reorderTimeoutRef.current = setTimeout(() => {
      const orders = reorderedTickets.map((t, i) => ({
        ticketId: t._id,
        kanban_order: i,
      }))
      reorderTickets(orders).catch(err => console.error('Failed to save order:', err))
    }, 500)
  }, [])

  const handleUpdateTicket = useCallback((ticketId: string, updates: Partial<Ticket>) => {
    setLocalTickets(prev => prev.map(t =>
      t._id === ticketId ? { ...t, ...updates } : t
    ))
  }, [])

  const getTicketsForStatus = (status: KanbanStatus) =>
    localTickets
      .filter(t => t.status === status)
      .sort((a, b) => (a.kanban_order || 0) - (b.kanban_order || 0))

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="kanban-board">
        {KANBAN_COLUMNS.map(col => (
          <KanbanColumn
            key={col.status}
            status={col.status}
            label={col.label}
            color={col.color}
            tickets={getTicketsForStatus(col.status)}
            tags={tags}
            onTicketClick={onTicketClick}
            onDrop={handleDrop}
            onReorder={handleReorder}
            onUpdateTicket={handleUpdateTicket}
            onRefresh={onRefresh}
          />
        ))}
      </div>
    </DndProvider>
  )
}

export default KanbanBoard
